if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      if (!this.hasAttribute('data-hush-motion-gallery') || this.hushHeightCleanup) return;
      const viewer = this.elements.viewer;
      const list = viewer?.querySelector('[id^="Slider-Gallery"]');
      if (!list) return;
      const slides = Array.from(list.querySelectorAll('[data-media-id]'));
      let frame;
      let selected;
      const fit = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          // Desktop uses its existing active-slide layout; only fit the mobile scroller.
          if (this.mql.matches) {
            list.style.removeProperty('height');
            list.style.removeProperty('overflow-y');
            return;
          }
          const visible = slides.filter(slide => slide.getBoundingClientRect().width > 0);
          const left = list.getBoundingClientRect().left;
          const current = selected || visible.reduce((nearest, slide) =>
            !nearest || Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(nearest.getBoundingClientRect().left - left) ? slide : nearest, null);
          if (!current) return;
          const height = current.getBoundingClientRect().height;
          if (height > 0) {
            list.style.height = `${Math.ceil(height)}px`;
            list.style.overflowY = 'hidden';
          }
        });
      };
      const onSlide = event => {
        if (event.detail?.currentElement?.parentElement !== list) return;
        selected = event.detail.currentElement;
        fit();
      };
      const onResize = () => { selected = null; fit(); };
      const observer = new ResizeObserver(onResize);
      slides.forEach(slide => observer.observe(slide));
      viewer.addEventListener('slideChanged', onSlide);
      this.mql.addEventListener('change', onResize);
      fit();
      this.hushHeightCleanup = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        viewer.removeEventListener('slideChanged', onSlide);
        this.mql.removeEventListener('change', onResize);
        list.style.removeProperty('height');
        list.style.removeProperty('overflow-y');
      };
    }

    disconnectedCallback() {
      this.hushHeightCleanup?.();
      this.hushHeightCleanup = null;
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      // Flavor defaults must not replace or reorder Hush's opening animation.
      // Manual thumbnail navigation passes false and keeps its native behavior.
      if (prepend && this.hasAttribute('data-hush-motion-gallery')) return;
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      // Variant offer graphics stay attached to products but are omitted from Hush's browsing gallery.
      if (!activeMedia && this.hasAttribute('data-hush-editorial-gallery')) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      if (activeItem.hasAttribute('data-hush-motion-slide')) {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.textContent = 'Floating Hush Mint and Vanilla tins';
        return;
      }
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
